import React, { useContext, useMemo, useState } from 'react';
import { Box, ResponsiveContext, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { IconChevronDown } from '@tabler/icons-react';
import styled from 'styled-components';
import Icon from '../../Icon';
import DateInput from '../../DateInput';
import AgingBalancesPieChart from '../../AgingBalancesPieChart';
import { collectionsHeadline, invoicesForMonth, lastWeeks, usesMedian, latestPaymentPeriod, paymentPeriodPoints, periodLabel, transactionsForMonth } from '../../../utils/chart-data';
import { ChartSwatch } from '../../charts/ChartFigure';
import { formatCurrencySI } from '../../../utils/numbers';
import ChartFigure from '../../charts/ChartFigure';
import ChartLink from '../../charts/ChartLink';
import PairedBars from '../../charts/PairedBars';
import PaymentLine from '../../charts/PaymentLine';
import { ChartWeeksSkeleton, SkeletonText } from '../../charts/ChartSkeleton';
import { CollectionsChart, REPORT, VIEW_REPORT, useCollections } from '../../charts/CollectionsCard';
import { formatDate } from '../../../utils/dates';

// Four numbers, each opening its chart under the strip. One open at a time, and the dashboard
// always starts on Collections. Only the open chart is drawn.

const Wrap = styled.div`container-type: inline-size;`;
// The 1px gap shows the frame's line color through, so tiles get dividers whichever way they wrap
const Tiles = styled.div`
    display: grid;
    gap: 1px;
    background: ${(props) => props.$line};
    grid-template-columns: minmax(0, 1fr);
    & > * { order: var(--order); }
    /* On a phone a closed tile is one line, and the open one shows its number and runs straight
       into its chart, so which one is open reads from the shape, not a color */
    [data-full] { display: none; }
    [data-open='true'] [data-full] { display: block; }
    [data-open='true'] [data-compact] { display: none; }
    & > [data-body] { margin-top: -1px; }
    @container (min-width: 900px) {
        grid-template-columns: repeat(var(--tiles), minmax(0, 1fr));
        & > [data-body] { order: 100; margin-top: 0; }
        [data-full] { display: block; }
        [data-compact] { display: none; }
        /* Side by side the chart opens under all four, so the open tile is underlined like a tab */
        [data-open='true'] { box-shadow: inset 0 -2px 0 ${(props) => props.$text}; }
    }
`;
// Under its own tile on a phone, under the whole strip when the tiles sit in a row
const Body = styled.div`grid-column: 1 / -1; background: ${(props) => props.$bg};`;
const TileButton = styled.button`
    all: unset;
    /* After the reset, which would otherwise clear it */
    order: var(--order);
    box-sizing: border-box;
    cursor: pointer;
    padding: 20px 24px;
    &:not([data-open='true']) { padding: 14px 24px; }
    @container (min-width: 900px) { &:not([data-open='true']) { padding: 20px 24px; } }
    /* The open tile keeps the card's background: a filled tile is a big block of color. Hover is
       the faint tint menus and list rows use. */
    background: ${(props) => props.$bg};
    &:hover { background: ${(props) => (props.$open ? props.$bg : props.$hoverBg)}; }
    &:focus-visible { outline: 2px solid ${(props) => props.$accent}; outline-offset: -2px; }
`;
const Chevron = styled.span`display: inline-flex; transition: transform .15s; transform: rotate(${(props) => (props.$open ? '180deg' : '0')});`;

const OVERDUE_BUCKETS = ['1', '31', '61', '91'];
const sum = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0);

// The chart alone is six thin pairs across the whole width, so the months sit beside it as numbers
const SalesSplit = styled.div`
    display: grid;
    gap: 24px 48px;
    grid-template-columns: minmax(0, 1fr);
    @container (min-width: 900px) { grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); }
`;
const MonthTable = styled.div`
    display: grid;
    grid-template-columns: auto 1fr 1fr;
    align-content: start;
    & > * { padding: 6px 0 6px 16px; border-bottom: 1px solid ${(props) => props.$line}; }
    & > *:nth-child(3n + 1) { padding-left: 0; }
`;

const SalesBody = ({ rows, currency, line }) => (
    <SalesSplit>
        <Box gap="small">
            <Box direction="row" gap="medium">
                <Box direction="row" gap="xsmall" align="center"><ChartSwatch color="chart-muted" /><Text size="small" color="text-weak">Sales</Text></Box>
                <Box direction="row" gap="xsmall" align="center"><ChartSwatch color="barchart-primary" /><Text size="small" color="text-weak">Payments</Text></Box>
            </Box>
            <PairedBars rows={rows} currency={currency} height={220} />
        </Box>
        <MonthTable $line={line}>
            <Text size="small" color="text-weak">Month</Text>
            <Text size="small" color="text-weak" textAlign="end">Sales</Text>
            <Text size="small" color="text-weak" textAlign="end">Payments</Text>
            {[...rows].reverse().map((row) => (
                <React.Fragment key={row.month}>
                    <Text>{row.month}</Text>
                    <Box align="end"><ChartLink href={invoicesForMonth(row.month)} label={<Text color="text">{formatCurrencySI(row.sales_amount || 0, currency)}</Text>} /></Box>
                    <Box align="end"><ChartLink href={transactionsForMonth(row.month)} label={<Text color="text">{formatCurrencySI(row.payment_amount || 0, currency)}</Text>} /></Box>
                </React.Fragment>
            ))}
        </MonthTable>
    </SalesSplit>
);

const DashboardStrip = ({ dashboardData, monthlySales, averagePaymentPeriod, currency, reportDate, setReportDate }) => {
    const theme = useContext(ThemeContext);
    const color = (name) => normalizeColor(name, theme);
    // Undefined until a tile is clicked: the first tile opens, which is Collections unless it failed
    const [chosen, setChosen] = useState();
    const isPhone = useContext(ResponsiveContext) === 'small';

    const { data: collections, error: collectionsError } = useCollections();
    // A failed report drops its tile, as the Collections card used to hide, rather than show $0.
    // Keyed on the error, since before the first request starts nothing is loading yet either.
    const collectionsFailed = collectionsError !== undefined && !collections;
    const shownWeeks = useMemo(() => (collections && isPhone ? lastWeeks(collections, 13) : collections), [collections, isPhone]);
    const points = useMemo(() => paymentPeriodPoints(averagePaymentPeriod?.data), [averagePaymentPeriod]);
    const latest = useMemo(() => latestPaymentPeriod(points), [points]);
    const balances = dashboardData?.aging_balances || {};
    // The backend's own figure where it sends one, from the same aging calculation
    const overdue = dashboardData?.overdue_receivables
        ?? OVERDUE_BUCKETS.reduce((total, bucket) => total + (balances[bucket]?.amount || 0), 0);
    const total = dashboardData?.total_receivables;
    const rows = monthlySales?.data || [];
    const months = rows.map((row) => row.month);
    const terms = averagePaymentPeriod?.company_net_terms;

    // A past "As of" date says so on the tile, which stays in view when the Aging chart is closed
    const asOf = reportDate ? formatDate(reportDate, false, true) : null;

    const tiles = [
        !collectionsFailed && {
            key: 'collections',
            title: 'Collections',
            figure: collections ? collectionsHeadline(collections) : { label: 'Collected last week', isLoading: true },
            action: VIEW_REPORT,
            // A year of weeks is a barcode at phone width, so a phone gets the last quarter
            body: collections ? <CollectionsChart report={shownWeeks} reportHref={REPORT} figures={false} /> : <ChartWeeksSkeleton />,
        },
        {
            key: 'aging',
            title: 'Aging balance',
            figure: {
                label: asOf ? `Receivables as of ${asOf}` : 'Total receivables',
                value: total ? formatCurrencySI(total, currency) : null,
                empty: asOf ? 'No open invoices on this date' : 'Appears once you send an invoice',
                note: total ? `${formatCurrencySI(overdue, currency)} overdue` : undefined,
                isLoading: !dashboardData,
            },
            action: <DateInput value={reportDate} onChange={setReportDate} labelPrefix="As of " defaultLabel="today" />,
            body: <AgingBalancesPieChart dashboardData={dashboardData} asOfDate={reportDate} isLoading={!dashboardData} wide />,
        },
        {
            key: 'sales',
            title: 'Monthly sales',
            figure: (() => {
                const sales = sum(rows, 'sales_amount');
                const paid = sum(rows, 'payment_amount');
                const has = sales > 0 || paid > 0;
                return { label: months.length ? `Sales, ${periodLabel(months)}` : 'Sales', value: has ? formatCurrencySI(sales, currency) : null, empty: 'Appears after your first invoice', note: has ? `${formatCurrencySI(paid, currency)} in payments` : undefined, isLoading: !monthlySales };
            })(),
            body: <SalesBody rows={rows} currency={currency} line={color('border-weak')} />,
        },
        {
            key: 'period',
            title: 'Days to pay',
            figure: { label: usesMedian(averagePaymentPeriod?.data) ? 'Typical days to pay' : 'Average days to pay', value: latest ? `${latest.days} days` : null, empty: 'Appears after your first invoice is paid', note: latest && [terms ? `Net ${terms}` : null, latest.note].filter(Boolean).join(', '), isLoading: !averagePaymentPeriod },
            body: <PaymentLine points={points} netTerms={terms} height={220} />,
        },
    ].filter(Boolean);
    const open = chosen === undefined ? tiles[0].key : chosen;
    const toggle = (key) => setChosen(open === key ? null : key);
    const current = tiles.find((tile) => tile.key === open);

    return (
        <Box round="xsmall" border elevation="medium" background="background" overflow="hidden">
            <Wrap>
                <Tiles $line={color('border-weak')} $text={color('text')} style={{ '--tiles': tiles.length }}>
                    {tiles.map((tile, index) => (
                        <TileButton
                            key={tile.key}
                            style={{ '--order': index * 2 }}
                            type="button"
                            aria-expanded={open === tile.key}
                            onClick={() => toggle(tile.key)}
                            data-open={open === tile.key}
                            $bg={color('background')}
                            $hoverBg={color('hover-subtle')}
                            $accent={color('focus')}
                        >
                            <Box data-full>
                                <Box direction="row" justify="between" align="start" gap="small">
                                    <ChartFigure {...tile.figure} />
                                    <Chevron $open={open === tile.key}><Icon SelectedIcon={IconChevronDown} color="text-weak" size="small" /></Chevron>
                                </Box>
                            </Box>
                            <Box data-compact direction="row" justify="between" align="center" gap="small">
                                <Text color="text-weak" truncate>{tile.figure.label}</Text>
                                <Box direction="row" align="center" gap="small" flex={false}>
                                    {tile.figure.isLoading ? <SkeletonText width="56px" /> : <Text weight={500}>{tile.figure.value}</Text>}
                                    <Chevron><Icon SelectedIcon={IconChevronDown} color="text-weak" size="small" /></Chevron>
                                </Box>
                            </Box>
                        </TileButton>
                    ))}
                    {current && (
                        <Body data-body style={{ '--order': tiles.indexOf(current) * 2 + 1 }} $bg={color('background')}>
                            <Box pad="medium" gap="medium">
                                <Box direction="row" justify="between" align="center" gap="small" wrap>
                                    <Text size="large" weight={500}>{current.title}</Text>
                                    {current.action}
                                </Box>
                                {current.body}
                            </Box>
                        </Body>
                    )}
                </Tiles>
            </Wrap>
        </Box>
    );
};

export default DashboardStrip;
